/**
 * 漢字 — jōyō 2,136 ตัว
 *
 * ข้อมูลมาจาก KANJIDIC2 + KRADFILE (ดู DATA-SOURCES.md) โหลดตอนเลือก deck ครั้งแรก
 * ไม่ bundle เพราะ 450 KB ไม่ควรถ่วงคนที่ยังอยู่ Phase 0
 *
 * ความหมายอังกฤษเป็นแค่ค่าตั้งต้น — แต่ง keyword ไทยทับได้ในแอป
 * ตรงกับ NOTES.md §9 ที่สรุปว่า "คิดเองจำดีกว่าของสำเร็จรูป"
 * (และเป็นเหตุผลที่ไม่ใช้ RTK keyword ซึ่งมีลิขสิทธิ์อยู่แล้ว)
 */
import type { Card } from '@/core/types'
import type { Deck, Group } from './types'
import { bushuDeck } from './bushu'
import { canonicalPart } from './aliases'
import { STROKE_SHARD_SIZE } from '@/core/strokes'

interface KanjiRow {
  c: string
  m: string[]
  /** ความหมายไทย — ไม่มีทุกตัว ตัวที่ยังไม่ได้แปลถอยไปใช้ m */
  th?: string
  on: string[]
  kun: string[]
  s: number
  g: number | null
  f: number | null
  jlpt: number | null
  r: string[]
}

const GRADE_LABEL: Record<number, string> = {
  1: 'ป.1',
  2: 'ป.2',
  3: 'ป.3',
  4: 'ป.4',
  5: 'ป.5',
  6: 'ป.6',
  8: 'มัธยม',
  9: 'ชื่อคน',
  10: 'ชื่อคน (รูปเก่า)',
}

/**
 * เรียงลำดับการเรียน — ปลดล็อกคันจิเมื่อรู้ทุกชิ้นส่วนที่ประกอบมันแล้ว
 *
 * นี่คือสิ่งที่ RTK เก่ง และสร้างเองได้จาก KRADFILE โดยไม่ต้องแตะ keyword ที่มีลิขสิทธิ์
 * ต่างจาก RTK ตรงที่ถ่วงด้วยความถี่จริงในหนังสือพิมพ์ด้วย — ในแต่ละรอบที่ปลดล็อกได้
 * ตัวที่เจอบ่อยกว่าจะมาก่อน จึงได้ใช้ของที่เรียนเร็วกว่า
 *
 * คันจิที่ปลดล็อกแล้วกลายเป็นชิ้นส่วนที่ "รู้แล้ว" ของตัวถัดไป
 * เช่น 謎 ประกอบจาก 言 込 米 — 込 ไม่ใช่ radical ในชุด 105 แต่มันประกอบจาก 辶 + 入
 * ที่รู้แล้ว จึงถูกปลดล็อกก่อน แล้ว 謎 ตามมาได้
 */
function learningOrder<T extends KanjiRow>(rows: T[], knownParts: Set<string>): T[] {
  const known = new Set(knownParts)
  const byFreq = [...rows].sort((a, b) => (a.f ?? 99_999) - (b.f ?? 99_999))

  const out: T[] = []
  let pending = byFreq

  while (pending.length > 0) {
    const ready: T[] = []
    const locked: T[] = []
    for (const k of pending) {
      if (k.r.every((part) => known.has(part))) ready.push(k)
      else locked.push(k)
    }
    if (ready.length === 0) break // รอบนี้ปลดไม่ได้เลย = ที่เหลือปลดไม่ได้อีกแล้ว

    out.push(...ready)
    // เพิ่มเข้า known หลังจบรอบ ไม่ใช่ระหว่างรอบ — ไม่งั้นลำดับขึ้นกับตำแหน่งใน array
    for (const k of ready) known.add(k.c)
    pending = locked
  }

  // ที่เหลือมีชิ้นส่วนที่ไม่เคยปลดล็อกได้ (radical นอกชุด 105) — ต่อท้ายตามความถี่
  out.push(...pending)
  return out
}

const cards: Card[] = []
let loadPromise: Promise<void> | null = null

async function load(): Promise<void> {
  if (cards.length > 0) return

  const res = await fetch(`${import.meta.env.BASE_URL}data/kanji-core.json`)
  if (!res.ok) throw new Error(`โหลดข้อมูลคันจิไม่ได้ — รัน npm run data:update ก่อน (${res.status})`)
  const all = (await res.json()) as KanjiRow[]

  /*
   * jōyō 2,136 ตัว (grade 1–8) คือชุดที่รัฐบาลญี่ปุ่นกำหนดว่าพอสำหรับอ่านสื่อทั่วไป
   * jinmeiyō (grade 9–10) อีก 838 ตัวใช้ตั้งชื่อคนโดยเฉพาะ — ไม่จำเป็นสำหรับอ่านข่าว
   * แต่จำเป็นถ้าอยากอ่านชื่อตัวละครและชื่อ VTuber ออก จึงโหลดมาทั้งหมด
   * แล้วให้เลือกด้วยชิป "ชื่อคน" เอา ไม่ได้ยัดใส่คิวตั้งแต่แรก
   *
   * แปลงชิ้นส่วนเป็นรูปที่เราสอนตั้งแต่ตรงนี้ ทั้งการจัดลำดับและตัวการ์ดจะได้ใช้ค่าเดียวกัน
   */
  const jouyou = all
    .filter((k) => k.g !== null)
    .map((k, i) => ({
      ...k,
      /*
       * KRADFILE ใส่ตัวอักษรเองเป็น radical ของตัวเองใน 159 ตัว (見 = 見 目 儿)
       * เพราะมันทำมาเพื่อค้นหา ไม่ใช่เพื่อเรียน · ทิ้งไว้จะเสียสองอย่าง:
       * กล่องเฉลยขึ้นว่า 見 ประกอบจาก 見 ซึ่งไม่ได้บอกอะไร และ learningOrder
       * ปลดล็อกมันไม่ได้เลย (ต้องรู้ 見 ก่อนถึงจะเรียน 見) จึงตกไปท้ายคิว
       * ทั้งที่ 見 อยู่อันดับความถี่ต้น ๆ
       */
      r: [...new Set(k.r.map(canonicalPart))].filter((p) => p !== k.c),
      // ลำดับขีดแบ่งไฟล์ตามลำดับในไฟล์นี้ (ดู scripts/build-strokes.ts)
      // จำไว้ก่อนจัดลำดับใหม่ ไม่งั้นหาไฟล์ที่ถูกไม่เจอ
      shard: Math.floor(i / STROKE_SHARD_SIZE),
    }))

  const knownParts = new Set<string>()
  for (const c of bushuDeck.cards) {
    knownParts.add(c.front)
    const v = c.extra?.['variant']
    if (typeof v === 'string' && v) knownParts.add(v)
  }

  const ordered = learningOrder(jouyou, knownParts)

  ordered.forEach((k, i) => {
    const tags = [`g${k.g}`]
    if (k.jlpt) tags.push(`jlpt-n${k.jlpt}`)
    // ใช้แท็กเดียวกับที่ filterCards กรองออกโดยดีฟอลต์ — ต้องเลือกเองถึงจะเข้าคิว
    if (k.g !== null && k.g > 8) tags.push('rare')

    cards.push({
      id: `kanji:${k.c}`,
      deck: 'kanji',
      tags,
      front: k.c,
      /*
       * ไทยมาก่อนถ้ามี — ตัวเลือกที่เป็นอังกฤษบังคับให้แปลในหัวก่อนตอบ ซึ่งเป็น
       * งานคนละอย่างกับที่กำลังฝึก · อังกฤษเลื่อนไปเป็น alts จึงยังตอบถูกได้
       * ถ้าพิมพ์มา และยังอยู่ใน extra.meanings ให้โหมดอื่นหยิบไปใช้
       */
      back: k.th || k.m[0] || '',
      alts: k.th ? k.m : k.m.slice(1),
      extra: {
        meanings: k.m,
        on: k.on,
        kun: k.kun,
        strokes: k.s,
        grade: k.g,
        freq: k.f,
        jlpt: k.jlpt,
        /** ชิ้นส่วนจาก KRADFILE แปลงเป็นรูปที่เราสอนแล้ว (ดู aliases.ts) */
        parts: k.r,
        /** ลำดับการเรียนที่คำนวณไว้ — ใช้จัดคิวใบใหม่ */
        order: i,
        /** ไฟล์ลำดับขีดที่ตัวนี้อยู่ */
        shard: k.shard,
      },
    })
  })
}

const groups: Group[] = [
  ...Object.entries(GRADE_LABEL).map(([g, label]) => ({
    tag: `g${g}`,
    label,
    section: 'ระดับ',
  })),
  ...[5, 4, 3, 2, 1].map((n) => ({
    tag: `jlpt-n${n}`,
    label: `N${n}`,
    section: 'JLPT',
  })),
]

export const kanjiDeck: Deck = {
  id: 'kanji',
  titleJa: '漢字ドリル',
  titleTh: 'จำความหมายคันจิ · jōyō 2,136 + ชื่อคน 838',
  modes: ['auto', 'lesson', 'recall', 'glyph-to-meaning', 'breakdown'],
  scheduling: 'srs',
  cards,
  groups,
  gridSection: 'ระดับ',
  ensureLoaded: () => {
    loadPromise ??= load()
    return loadPromise
  },
}
